import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  Typography,
  Autocomplete,
  Avatar,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import {
  ArticleOutlined,
  CreateOutlined,
  ContentCopyOutlined,
  SaveOutlined,
  OpenInNew,
  Close
} from "@mui/icons-material";
import LoadingOverlay from "../Common/LoadingOverlay";
import CustomButton from "../Common/CustomButton";
import { MixpanelService } from "../../utils/mixpanelService";
import DeleteScenarioModal from "./DeleteScenarioModal";
import Status from "../Common/Status";

import {
  updateTestScenario,
  cloneTestScenario,
  deleteTestScenario,
} from "../../redux-store/testScenarioReducers/testScenarioActions";
import { useSnackbar } from "../../contexts/CustomSnackbarContext";

import "../../styles/components/Common/EditDetail.scss";

const EditDetail = ({ projectId, testScenarioId, closeDrawer }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [detail, setDetail] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [errors, setErrors] = useState({});
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [tagText, setTagText] = useState(false);
  const { openSnackbar } = useSnackbar();

  const selectedTestScenarioDetail = useSelector(
    (state) => state.testScenario.selectedTestScenarioDetail
  );

  const isLoading = useSelector((state) => state.testScenario.isLoading);
  const isSaving = useSelector((state) => state.testScenario.isSaving);
  const allTags = useSelector((state) => state.testScenario.testScenarioTags);
  const allOwners = useSelector((state) => state.user.owners);

  const allStatuses = ["draft", "active", "archived"];

  const styles = {
    container: {
      p: 2,
      px: 4,
      color: "var(--text-primary)",
      backgroundColor: "var(--background-primary)",
    },
    section: {
      marginBottom: "24px",
    },
    sectionTitle: {
      color: "var(--text-secondary)",
      marginBottom: "8px",
    },
    description: {
      width: "100%",
      padding: "8px",
      borderRadius: "4px",
      border: "1px solid var(--borderColor)",
      backgroundColor: "var(--background-primary)",
      color: "var(--text-primary)",
      resize: "vertical",
      "&:focus": {
        outline: "none",
        borderColor: "var(--primary-color)",
      },
    },
    divider: {
      borderBottom: "1px solid var(--borderColor)",
      width: "100%",
    },
    dangerZone: {
      color: "#d32f2f",
    },
    errorText: {
      color: "#d32f2f",
    },
    chip: {
      mr: 1,
      mt: 1,
      backgroundColor: "var(--background-secondary)",
      color: "var(--text-primary)",
      "& .MuiChip-deleteIcon": {
        color: "var(--text-secondary)",
        "&:hover": {
          color: "var(--text-primary)",
        },
      },
    },
    textField: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "var(--background-primary)",
        color: "var(--text-primary)",
        "& fieldset": {
          borderColor: "var(--borderColor)",
        },
        "&:hover fieldset": {
          borderColor: "var(--primary-color-light)",
        },
        "&.Mui-focused fieldset": {
          borderColor: "var(--primary-color)",
        },
      },
      "& .MuiInputLabel-root": {
        color: "var(--text-secondary)",
      },
      "& .MuiInputBase-input": {
        color: "var(--text-primary)",
      },
    },
  };

  useEffect(() => {
    setInitialState();
  }, []);

  const setInitialState = () => {
    const detailObj = {
      title: selectedTestScenarioDetail?.title || "",
      description: selectedTestScenarioDetail?.description || "",
      tags: selectedTestScenarioDetail?.tags || [],
      owner: selectedTestScenarioDetail?.owner || {},
      status: selectedTestScenarioDetail?.status || "draft",
    };
    setDetail(detailObj);
  };

  const updateTestScenarioDetail = async (data) => {
    await dispatch(updateTestScenario({ testScenarioId, data: data }));
    setIsEdit(false);
    openSnackbar({
      message: "Test scenario updated successfully.",
      severity: "success",
    });
  };

  const onChange = (name, value) => {
    if (name === "title") updateErrors("title", "");
    setDetail((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const updateErrors = (key, msg = "") => {
    setErrors((prevState) => {
      const newState = { ...prevState };
      newState[key] = msg;
      if (!msg) delete newState[key];
      return newState;
    });
  };

  const onOwnerChange = (event, newValue) => {
    setDetail((prevState) => ({
      ...prevState,
      owner: newValue,
    }));
  };

  const onEdit = () => {
    setInitialState();
    setIsEdit(true);
  };

  const onClone = async () => {
    /** @todo : before cloning | Show a warning prompt to save data */
    closeDrawer();
    const response = await dispatch(
      cloneTestScenario({ testScenarioId: testScenarioId })
    );
    if (response?.payload?.id) {
      openSnackbar({
        message: "Test scenario cloned successfully.",
        severity: "success",
      });
      history.push(`/test-scenarios/${response.payload.id}/`);
    } else {
      openSnackbar({
        message: "Test scenario cloning failed!",
        severity: "error",
      });
    }
  };

  const toggleArchiveModal = () => {
    setShowArchiveModal((prevState) => !prevState);
  };

  const onArchiveTestScenario = async () => {
    await dispatch(
      updateTestScenario({
        testScenarioId: testScenarioId,
        data: { status: "archived" },
      })
    );
    openSnackbar({
      message: "Test scenario archived successfully.",
      severity: "success",
    });
    toggleArchiveModal();
  };

  const onDeleteTestScenario = async () => {
    await dispatch(deleteTestScenario({ testScenarioId: testScenarioId }));
    MixpanelService.track("test_scenario_deleted", { test_scenario_id: testScenarioId });
    openSnackbar({
      message: "Test scenario deleted successfully.",
      severity: "success",
    });
    closeDrawer();
    history.push("/test-scenarios/");
  };

  const onSave = () => {
    const formattedDetail = {
      ...detail,
      owner_id: detail?.owner?.id,
    };
    let error = false;
    if (!formattedDetail?.owner_id) {
      updateErrors("owner", "Required");
      error = true;
    }
    if (!formattedDetail?.title || !formattedDetail.title.trim()) {
      updateErrors("title", "Required");
      error = true;
    }
    if (error) {
      return;
    }
    updateTestScenarioDetail(formattedDetail);
  };

  const onCancelEdit = () => {
    setIsEdit(false);
    setInitialState();
  };

  const handleDeleteTag = (newValue) => {
    setDetail((prevState) => ({
      ...prevState,
      tags: [...prevState.tags.filter((tag) => newValue !== tag)],
    }));
  };

  const onChangeTag = (event, newValue) => {
    if (newValue && newValue.trim() && detail?.tags?.indexOf(newValue) === -1) {
      setDetail((prevState) => ({
        ...prevState,
        tags: [...prevState.tags, newValue],
      }));
    } else {
      event.preventDefault();
    }
  };

  const onStatusChange = (event, newValue) => {
    setDetail((prevState) => ({
      ...prevState,
      status: newValue,
    }));
  };

  const getFilteredTags = () => {
    return allTags.filter((tag) => detail?.tags?.indexOf(tag) === -1);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && tagText && tagText.trim()) {
      event.preventDefault();
      event.stopPropagation();
      const trimmedTag = tagText.trim();
      const tagIndex = detail.tags.findIndex(
        (tag) => tag.toLowerCase() === trimmedTag.toLowerCase()
      );
      if (tagIndex >= 0) {
        return;
      }
      setDetail((prevState) => ({
        ...prevState,
        tags: [
          ...prevState.tags,
          trimmedTag[0].toUpperCase() + trimmedTag.slice(1),
        ],
      }));
      setTagText("");
    }
  };

  if (isLoading) {
    return <LoadingOverlay isLoading={isLoading} />;
  }

  let content = null;
  content = (
    <>
      <div className="section" style={styles.section}>
        <h6 style={styles.sectionTitle}>ID</h6>
        {isEdit ? (
          <TextField
            sx={{ ...styles.textField, mb: 2, width: "100%" }}
            disabled
            size="small"
            variant="outlined"
            value={selectedTestScenarioDetail?.code}
          />
        ) : (
          <Typography>{selectedTestScenarioDetail?.code}</Typography>
        )}
      </div>
      <div className="section" style={styles.section}>
        <h6 style={styles.sectionTitle}>Title</h6>
        {isEdit ? (
          <TextField
            sx={{ ...styles.textField, mb: 2, width: "100%" }}
            size="small"
            variant="outlined"
            value={detail?.title}
            onChange={(e) => {
              onChange("title", e.target.value);
            }}
          />
        ) : (
          <Typography>{selectedTestScenarioDetail?.title}</Typography>
        )}
        {errors?.title && (
          <Typography sx={styles.errorText}>{errors.title}</Typography>
        )}
      </div>
      <div className="section" style={styles.section}>
        <h6 style={styles.sectionTitle}>Description</h6>
        {isEdit ? (
          <textarea
            rows={3}
            style={styles.description}
            value={detail?.description}
            onChange={(e) => {
              onChange("description", e.target.value);
            }}
          />
        ) : (
          <div className="truncate-2-lines description" style={{ color: "var(--text-primary)" }} title={selectedTestScenarioDetail?.description}>
            {selectedTestScenarioDetail?.description}
          </div>
        )}
        {errors?.description && (
          <Typography sx={styles.errorText}>{errors.description}</Typography>
        )}
      </div>
      <div className="section" style={styles.section}>
        <h6 style={styles.sectionTitle}>Status</h6>
        <RadioGroup
          aria-labelledby="status-radio-buttons-group-label"
          onChange={onStatusChange}
          value={isEdit ? detail.status : selectedTestScenarioDetail.status}
        >
          {!isEdit && (
            allStatuses.filter(
              (status) => status === selectedTestScenarioDetail.status
            )
            ).map((status) => (
              <Status status={status} />
            )
          )}
          {isEdit && allStatuses.map((status) => (
            <FormControlLabel
              key={status}
              sx={{ height: "30px", color: "var(--text-primary)" }}
              value={status}
              control={<Radio sx={{ color: "var(--primary-color)" }} />}
              label={(<Status status={status} />)}
            />
          ))}
        </RadioGroup>
      </div>
      <div className="section" style={styles.section}>
        <h6 style={styles.sectionTitle}>Owner</h6>
        {isEdit ? (
          <Autocomplete
            options={allOwners}
            getOptionLabel={(option) => option.name || option.email}
            onChange={onOwnerChange}
            value={detail?.owner}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ ...styles.textField, mt: 1 }}
                size="small"
                variant="outlined"
              />
            )}
          />
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              alt={selectedTestScenarioDetail?.owner?.name}
              src={selectedTestScenarioDetail?.owner?.avatar}
              sx={{ width: 24, height: 24, bgcolor: "var(--agent-avatar)" }}
            />
            <Typography variant="body1">
              {selectedTestScenarioDetail?.owner?.name}
            </Typography>
          </Stack>
        )}
        {errors?.owner && (
          <Typography sx={styles.errorText}>{errors.owner}</Typography>
        )}
      </div>
      <div className="tagsContainer section" style={styles.section}>
        <h6 style={styles.sectionTitle}>Tags</h6>
        {isEdit ? (
          <div className="tags">
            {detail?.tags?.map((tag) => (
              <Chip
                key={tag}
                sx={styles.chip}
                label={tag}
                onDelete={() => {
                  handleDeleteTag(tag);
                }}
              />
            ))}
            <Autocomplete
              options={getFilteredTags()}
              onChange={onChangeTag}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ ...styles.textField, mt: 1 }}
                  onChange={(e) => {
                    setTagText(e.target.value);
                  }}
                  value={tagText}
                  onKeyDown={handleKeyDown}
                  size="small"
                  variant="outlined"
                />
              )}
            />
          </div>
        ) : (
          <div>
            {selectedTestScenarioDetail?.tags?.map((tag) => (
              <Chip key={tag} sx={styles.chip} label={tag} />
            ))}
          </div>
        )}
      </div>
      {!isEdit && (
        <>
          <div className="section" style={styles.section}>
            <h6 style={styles.sectionTitle}>Associated Test Suites</h6>
            {selectedTestScenarioDetail?.test_suite_scenarios?.length === 0 &&
              "No associated test suites"}
            {selectedTestScenarioDetail?.test_suite_scenarios?.map(
              (testSuiteScenario) => (
                <div key={testSuiteScenario.id} style={{ color: "var(--text-primary)" }}>
                  {testSuiteScenario?.test_suite?.title}
                  <OpenInNew
                    sx={{ cursor: "pointer", color: "var(--text-secondary)" }}
                    onClick={() => {
                      window.open(
                        `${window.location.origin}/test-suites/${testSuiteScenario?.test_suite?.id}`,
                        "_blank"
                      );
                    }}
                  />
                </div>
              )
            )}
          </div>
          <div className="section" style={styles.section}>
            <h6 style={styles.sectionTitle}>Last Update</h6>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Avatar
                alt={selectedTestScenarioDetail?.updated_by?.name}
                src={selectedTestScenarioDetail?.updated_by?.avatar}
                sx={{ width: 24, height: 24, bgcolor: "var(--agent-avatar)" }}
              />
              <Typography variant="body1">
                {selectedTestScenarioDetail?.updated_by?.name}
              </Typography>
            </Stack>
            {selectedTestScenarioDetail?.updated_at && (
              <Typography sx={{ color: "var(--text-secondary)" }}>
                {new Date(selectedTestScenarioDetail.updated_at * 1000).toString()}
              </Typography>
            )}
          </div>
          <div className="section" style={styles.section}>
            <h6 style={styles.sectionTitle}>Created At</h6>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Avatar
                alt={selectedTestScenarioDetail?.created_by?.name}
                src={selectedTestScenarioDetail?.created_by?.avatar}
                sx={{ width: 24, height: 24, bgcolor: "var(--agent-avatar)" }}
              />
              <Typography variant="body1">
                {selectedTestScenarioDetail?.created_by?.name}
              </Typography>
            </Stack>
            {selectedTestScenarioDetail?.created_at && (
              <Typography sx={{ color: "var(--text-secondary)" }}>
                {new Date(selectedTestScenarioDetail.created_at * 1000).toString()}
              </Typography>
            )}
          </div>
        </>
      )}
    </>
  );

  const nonEditModeActions = (
    <Grid container spacing={2} sx={{ flexDirection: "row-reverse" }}>
      <CustomButton
        variant="outlined"
        color="primary"
        disabled={false}
        onClick={onEdit}
      >
        <CreateOutlined sx={{ mr: 1 }} />
        Edit
      </CustomButton>
      <CustomButton
        sx={{ mr: 2 }}
        variant="outlined"
        color="primary"
        disabled={false}
        onClick={onClone}
      >
        <ContentCopyOutlined sx={{ mr: 1 }} />
        Clone
      </CustomButton>
    </Grid>
  );

  const editModeActions = (
    <Grid container spacing={2}>
      <CustomButton
        variant="outlined"
        sx={{ mr: 2 }}
        color="primary"
        disabled={isSaving}
        onClick={onSave}
      >
        <SaveOutlined sx={{ mr: 1 }} />
        Save
      </CustomButton>
      <CustomButton
        variant="outlined"
        color="primary"
        disabled={isSaving}
        onClick={onCancelEdit}
      >
        <Close sx={{ mr: 1 }} />
        Cancel
      </CustomButton>
    </Grid>
  );

  return (
    <Box sx={styles.container} className="editDetail">
      {isSaving && <LoadingOverlay isLoading={isSaving} />}
      <Grid container spacing={2}>
        <Grid item xs={6} md={6}>
          {isEdit ? (
            <>
              <h5 style={{ color: "var(--text-primary)" }}>
                <CreateOutlined sx={{ mr: 1, color: "var(--primary-color)" }} />
                Edit Basic Details
              </h5>
            </>
          ) : (
            <>
              <h5 style={{ color: "var(--text-primary)" }}>
                <ArticleOutlined sx={{ mr: 1, color: "var(--primary-color)" }} />
                Basic Details
              </h5>
            </>
          )}
        </Grid>
        <Grid item xs={6} md={6}>
          {!isEdit && nonEditModeActions}
        </Grid>
        <Grid item xs={12} md={12}>
          {content}
        </Grid>
        <Grid item xs={12} md={12} style={{ padding: "32px 40px" }}>
          {isEdit && editModeActions}
        </Grid>
        {!isEdit && (
          <div style={styles.divider}></div>
        )}
        <Grid item xs={12} md={12}>
          {!isEdit && (
            <>
              <h5 style={styles.dangerZone}>Danger Zone</h5>
              <Button
                variant="contained"
                sx={{ width: "100%" }}
                color="error"
                disabled={false}
                onClick={toggleArchiveModal}
              >
                Delete
              </Button>
            </>
          )}
        </Grid>
      </Grid>
      {showArchiveModal && (
        <DeleteScenarioModal
          onClose={toggleArchiveModal}
          onArchive={onArchiveTestScenario}
          onDelete={onDeleteTestScenario}
        />
      )}
    </Box>
  );
};

export default EditDetail;
